import { useEffect, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import ErrorState from '../components/ErrorState.jsx'
import { api } from '../lib/api.js'

/**
 * `/settings/export/:id?token=…` — where the "your data is ready" email lands.
 *
 * A page rather than a redirect, and public rather than behind the session,
 * for the same two reasons: the archive has to be reachable by somebody whose
 * account is on its way out and has no session left, and a link in an email
 * that downloads a file the instant it is opened is a file downloaded by a
 * mail scanner. The button is the consent.
 *
 * Nothing is claimed until the server has been asked (DEC-2026-08-09-006).
 * The check is read-only and answers every bad link identically, so a mail
 * scanner may follow this URL as often as it likes and learn nothing by it —
 * and the person reading their email is told which of the two things went
 * wrong when it does: a link that is finished, or a server that did not
 * answer. Those must not read the same, because only one of them is worth
 * pressing a button about.
 */
export default function ExportDownload() {
  const { id } = useParams()
  const [params] = useSearchParams()
  const token = params.get('token')
  const [verdict, setVerdict] = useState('checking')
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    document.title = 'Your data — Sonologue'
  }, [])

  useEffect(() => {
    if (!token) return undefined
    let live = true
    setVerdict('checking')
    api
      .exportPreflight(id, token)
      .then(() => {
        if (live) setVerdict('ready')
      })
      .catch((error) => {
        if (!live) return
        // 404 is the server's definitive no — it looked, and this link is not
        // one it will serve. A 429, a 500 or a dead connection means it never
        // got to look, and saying "this link is finished" for that would
        // condemn a good link over a bad minute.
        if (error?.status !== 404) setVerdict('unreachable')
        else setVerdict(error.code === 'export-expired' ? 'expired' : 'invalid')
      })
    return () => {
      live = false
    }
  }, [id, token, attempt])

  // Without a token there is nothing to download, and announcing "your data is
  // ready" anyway is a claim this page cannot support — it made it for any id
  // at all, invented ones included, above a warning to treat the link like a
  // password. The three other pages that arrive by email (`/reset`, `/verify`,
  // `/account/cancel-deletion`) each say the link is incomplete and where a
  // whole one comes from; this says the same thing in the same shape.
  //
  // First, and without asking the server anything: an incomplete link is not a
  // question the server can answer, and a request carrying no token is one
  // more entry in an access log for nothing.
  if (!token) {
    return (
      <div className="card card-pad">
        <div className="empty-state">
          <h1 className="title-2">Your data</h1>
          <p>That link is missing its token. Use the whole link from the email.</p>
          <p className="footnote">
            If it has expired, ask for another from <Link to="/settings">Settings</Link>.
          </p>
        </div>
      </div>
    )
  }

  if (verdict === 'checking') {
    return (
      <div className="card card-pad">
        <div className="empty-state">
          <h1 className="title-2">Your data</h1>
          <p role="status">Checking your link…</p>
        </div>
      </div>
    )
  }

  // Retryable, and visibly so. The other two failures are the end of the road;
  // this one is a button press away from not being a failure at all.
  if (verdict === 'unreachable') {
    return (
      <ErrorState
        title="Your data"
        message="Sonologue can’t check this link right now."
        onRetry={() => setAttempt((n) => n + 1)}
        level={1}
      />
    )
  }

  if (verdict !== 'ready') {
    return (
      <div className="card card-pad">
        <div className="empty-state">
          <h1 className="title-2">Your data</h1>
          {verdict === 'expired' ? (
            // Only reachable for somebody whose token was once good: the
            // server tells nobody else that this export ever existed, so a
            // stranger gets the other sentence for every kind of bad link.
            <p>
              This link has expired. Your archive was deleted after 7 days. Ask for a new export
              from <Link to="/settings">Settings</Link>.
            </p>
          ) : (
            <p>
              This link isn’t valid anymore. It may have expired — archives are deleted after a few
              days. Ask for a fresh one from <Link to="/settings">Settings</Link>.
            </p>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="card card-pad">
      <div className="empty-state">
        <h1 className="title-2">Your data is ready</h1>
        <p>
          Everything Sonologue holds about your account, as JSON and CSV in one zip. The link
          expires — if it has, ask for another from <Link to="/settings">Settings</Link>.
        </p>
        <a className="btn btn-primary" href={api.exportDownloadUrl(id, token)}>
          Download the archive
        </a>
        <p className="footnote">
          Anyone with this link can download it. Treat it like a password.
        </p>
      </div>
    </div>
  )
}
